import { useStore } from '@tanstack/react-form'
import { useMutation } from '@tanstack/react-query'
import { LakehouseConnectionTypeEnum } from '@vendia/management-api-types'
import { ListS3FilesDocument } from '@vendia/control-plane-api'
import debug from 'debug'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import Collapsible from 'src/components/containers/collapsible'
import Alert from 'src/components/messages/alert'
import { SUPPORTED_ANALYTICAL_AWS_REGIONS } from 'src/utils/csp/regions'
import { filterS3IngestionFiles } from 'src/utils/lakehouse/connection'

import Button from '../../../components/buttons/button'
import Card from '../../../components/containers/card'
import ListboxField from '../../../components/fields/listbox.field'
import TextField from '../../../components/fields/text.field'
import { ScrollableStepContent } from '../../../components/flows/scrollable-step-content'
import StepButtons from '../../../components/flows/step-buttons'
import { StepContentHeader } from '../../../components/flows/step-header'
import { StepWrapper } from '../../../components/flows/step-wrapper'
import { StepComponent } from '../../../components/flows/types'
import BasicModal from '../../../components/modals/modal.basic'
import { createOnChangeValidator, isRequiredValidator } from '../../../utils/form/validation'
import useControlPlaneApi from 'src/utils/hooks/use-control-plane-api'
import { useFirstFocus } from '../../../utils/hooks/use-first-focus'
import { useGetUni } from '../../../utils/hooks/use-get-uni'
import { LakehouseFlowStepValues } from '../../../utils/lakehouse/types'
import { lakehouseFlowFieldValidator } from '../../../utils/lakehouse/validators'
import notify from '../../../utils/notify'
import { StepId } from '../config'

const logger = debug('app:S3')

const handleError = (error: string) => {
  if (error.includes('PermanentRedirect')) {
    notify.error(
      'Could not connect to the S3 bucket, likely because the bucket region does not match the region of the bucket you provided. Please select the correct region.',
    )
  } else {
    notify.error(`Error testing S3 connection: ${error}`)
  }
}

export const StepConnectS3: StepComponent<LakehouseFlowStepValues> = ({ context }) => {
  const form = context.form
  const client = useControlPlaneApi()
  const navigate = useNavigate()
  const prefix = 'source.s3'
  const nameRef = useFirstFocus()

  const { uniName, nodeName } = useStore(form.store, (s) => s.values)
  const connection = useStore(form.store, (s) => s.values.source.connection)

  const s3 = useStore(form.store, (s) => s.values.source?.s3)

  const { data: uniData } = useGetUni()
  const uni = uniData?.getUni
  const currentNode = uni?.nodes?.find((node) => node.name === nodeName)
  const accountId = currentNode?.vendiaAccount?.accountId
  const orgAccountId = currentNode?.vendiaAccount?.org

  const [showTrustModal, setShowTrustModal] = useState(false)

  function onClickTrustButton(e: any) {
    setShowTrustModal(true)
  }

  function hideTrustModal() {
    setShowTrustModal(false)
  }

  const getFilesMutation = useMutation({
    mutationFn: () =>
      client.graphqlFetch({
        query: ListS3FilesDocument,
        variables: {
          input: {
            uniName: uniName!,
            nodeName: nodeName!,
            bucket: s3?.bucketName!,
            bucketRegion: s3?.bucketRegion!,
            connectionInput: {
              type: LakehouseConnectionTypeEnum.S3,
              s3Connection: {
                crossAccountRoleArn: s3?.crossAccountRoleArn!,
                ...(s3?.encryptionKey && { encryptionKey: s3.encryptionKey }),
              },
            },
          },
        },
      }),
    onError: (error) => notify.error(`Error testing S3 connection: ${error}`),
    onSuccess: (response) => {
      logger('S3 connection response', response)
      if (response.errors) {
        handleError(response.errors[0].message)
        return
      }
      if (!response.listS3Files) {
        notify.error('Error testing S3 connection')
        return
      }
      const filteredFiles = filterS3IngestionFiles(response.listS3Files.objects)
      form.setFieldValue('source.availableFiles', filteredFiles)
      context.goToStep(StepId.SelectS3File)
    },
  })

  return (
    <StepWrapper>
      <StepContentHeader
        centered
        large
        title='Connect to Amazon S3'
        description='Provide the role ARN and bucket information to access your data files'
      />
      <ScrollableStepContent inset autoHeight>
        <Card padding='xl' className='mx-8 w-full max-w-4xl' childrenWrapperClassName='flex w-full flex-col gap-4'>
          <div className='mb-4'>
            <Alert kind='info'>
              <div className='flex justify-between'>
                <span className='min-w-0 flex-1'>
                  Before connecting, ensure your Amazon S3 role has trust relationships for accounts {accountId} and{' '}
                  {orgAccountId}.{' '}
                </span>
                <Button kind='anchor' onClick={onClickTrustButton} className='nowrap flex-shrink-0 !text-[inherit]'>
                  Instructions ›
                </Button>
              </div>
            </Alert>
          </div>

          <BasicModal
            className='sm:max-w-2xl'
            title='Update Trust Relationship'
            isOpen={showTrustModal}
            onClose={hideTrustModal}
            buttons={
              <div className='flex gap-2'>
                <Button kind={'secondary'} onClick={hideTrustModal} type='button'>
                  Close
                </Button>
              </div>
            }
          >
            <p className='text-dark mb-3'>
              Allow Vendia to access your S3 bucket by adding a trust relationship to your IAM role. This grants
              Vendia's AWS account permission to assume your role and access the S3 bucket on your behalf. An example of
              the trust relationship policy is shown below with the appropriate account ID.
            </p>
            <ol className='text-light mb-6 list-inside list-decimal text-sm'>
              <li>Go to the AWS IAM console</li>
              <li>Find the role you're using for Vendia access</li>
              <li>Click on the "Trust relationships" tab</li>
              <li>Click "Edit trust policy"</li>
              <li>Add or merge the trust relationship policy found below</li>
              <li>Click "Update trust policy"</li>
            </ol>
            <pre className='bg-dark overflow-x-auto rounded-lg p-6 text-xs text-white'>
              <code>
                {JSON.stringify(
                  {
                    Version: '2012-10-17',
                    Statement: [
                      {
                        Effect: 'Allow',
                        Principal: {
                          AWS: [`arn:aws:iam::${accountId}:root`, `arn:aws:iam::${orgAccountId}:root`],
                        },
                        Action: 'sts:AssumeRole',
                      },
                    ],
                  },
                  null,
                  2,
                )}
              </code>
            </pre>

            <p className='text-dark mb-3 mt-6'>
              Additionally, ensure your IAM role has the necessary S3 permissions to access your bucket. For more
              examples, click{' '}
              <a
                href='https://docs.aws.amazon.com/AmazonS3/latest/userguide/example-bucket-policies.html'
                target='_blank'
                rel='noopener noreferrer'
                className='text-blue-600 underline hover:text-blue-800'
              >
                here
              </a>
              :
            </p>
            <pre className='bg-dark overflow-x-auto rounded-lg p-6 text-xs text-white'>
              <code>
                {JSON.stringify(
                  {
                    Version: '2012-10-17',
                    Statement: [
                      {
                        Effect: 'Allow',
                        Action: ['s3:GetObject', 's3:ListBucket'],
                        Resource: ['arn:aws:s3:::your-bucket-name', 'arn:aws:s3:::your-bucket-name/*'],
                      },
                    ],
                  },
                  null,
                  2,
                )}
              </code>
            </pre>
          </BasicModal>
          <TextField
            ref={nameRef}
            name={`connectionName`}
            label='Name'
            description='Easily identify and reuse this connection to ingest additional files.'
            form={form}
            maxLength={64}
            useNestedLabel
            validators={createOnChangeValidator(
              {
                pattern: /^(?!\s*$).{1,64}$/,
                message: 'The connection name must be between 1 and 64 characters long.',
              },
              true,
            )}
          />
          <TextField
            name={`${prefix}.crossAccountRoleArn`}
            label='Role ARN'
            description='The ARN of the AWS role that Vendia will assume to access your S3 bucket'
            placeholder='arn:aws:iam::123456789012:role/VendiaS3AccessRole'
            form={form}
            useNestedLabel
            validators={lakehouseFlowFieldValidator({
              stepId: StepId.ConnectS3,
              listenTo: ['source.connection'],
              validator: isRequiredValidator(null, connection === LakehouseConnectionTypeEnum.S3),
            })}
          />
          <TextField
            name={`${prefix}.bucketName`}
            label='S3 Bucket Name'
            description='The name of the S3 bucket containing your CSV file'
            placeholder='my-data-bucket'
            form={form}
            useNestedLabel
            validators={lakehouseFlowFieldValidator({
              stepId: StepId.ConnectS3,
              listenTo: ['source.connection'],
              validator: isRequiredValidator(null, connection === LakehouseConnectionTypeEnum.S3),
            })}
          />
          <ListboxField
            name={`${prefix}.bucketRegion`}
            label='Bucket Region'
            description='The AWS region where your S3 bucket is located'
            options={SUPPORTED_ANALYTICAL_AWS_REGIONS}
            defaultValue='us-east-1'
            form={form}
            useNestedLabel
            validators={lakehouseFlowFieldValidator({
              stepId: StepId.ConnectS3,
              listenTo: ['source.connection'],
              validator: isRequiredValidator(null, connection === LakehouseConnectionTypeEnum.S3),
            })}
          />
          <Collapsible title={'Advanced settings'} description={'(optional)'} forceMountContent>
            <div className='mt-4'>
              <TextField
                name={`${prefix}.encryptionKey`}
                label='Encryption Key (Optional)'
                description='Encryption master key of client side encryption (CSE)'
                form={form}
                useNestedLabel
              />
            </div>
          </Collapsible>
        </Card>
      </ScrollableStepContent>
      <form.Subscribe selector={(state) => [state.canSubmit, state.isPristine]}>
        {([canSubmit, isPristine]) => (
          <StepButtons
            context={context}
            isSubmitting={getFilesMutation.isPending}
            nextBlocked={getFilesMutation.isPending || !canSubmit || isPristine}
            hasCancel={true}
            onCancel={() => navigate('../', { relative: 'path' })}
            onNext={async () => {
              const fieldErrors = await form.validateAllFields('change')
              if (fieldErrors.length > 0) {
                return { fieldErrors, formErrors: {}, hasErrors: true, isValid: false }
              }
              getFilesMutation.mutate()
            }}
          />
        )}
      </form.Subscribe>
    </StepWrapper>
  )
}
